import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Play, Clock, CheckCircle, XCircle, Loader } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ status }) {
  if (status === 'success') return (
    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1">
      <CheckCircle size={11} /> Success
    </Badge>
  );
  if (status === 'failed') return (
    <Badge className="bg-red-100 text-red-800 border-red-200 gap-1">
      <XCircle size={11} /> Failed
    </Badge>
  );
  if (status === 'running') return (
    <Badge className="bg-blue-100 text-blue-800 border-blue-200 gap-1 animate-pulse">
      <Loader size={11} className="animate-spin" /> Running
    </Badge>
  );
  return <Badge variant="outline">{status}</Badge>;
}

function fmt(dtStr) {
  if (!dtStr) return '—';
  return new Date(dtStr).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
}

function JobCard({ job }) {
  return (
    <div className="job-card" data-testid={`job-card-${job.job_name}`}>
      <div className="job-header">
        <h3 className="job-name">{job.job_name.replace('sync_', '').replace('_', ' ').toUpperCase()}</h3>
        <StatusBadge status={job.status} />
      </div>
      <div className="job-stats">
        <div className="stat"><span>Processed</span><strong>{(job.rows_processed || 0).toLocaleString()}</strong></div>
        <div className="stat"><span>Inserted</span><strong>{(job.rows_inserted || 0).toLocaleString()}</strong></div>
        <div className="stat"><span>Updated</span><strong>{(job.rows_updated || 0).toLocaleString()}</strong></div>
      </div>
      <div className="job-times">
        <div><Clock size={12} /> Started: {fmt(job.started_at)}</div>
        <div><Clock size={12} /> Finished: {fmt(job.finished_at)}</div>
      </div>
      {job.error_message && (
        <div className="job-error" data-testid="job-error">{job.error_message}</div>
      )}
    </div>
  );
}

export default function SyncStatus() {
  const [jobs, setJobs] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState('');
  const [message, setMessage] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const [sRes, hRes] = await Promise.all([
        fetch(`${API}/api/sync/status`, { credentials: 'include' }),
        fetch(`${API}/api/sync/history?limit=20`, { credentials: 'include' }),
      ]);
      if (sRes.ok) {
        const d = await sRes.json();
        setJobs(d.jobs || []);
      }
      if (hRes.ok) {
        const d = await hRes.json();
        setHistory(d.history || []);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 15000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const trigger = async (type) => {
    setTriggering(type);
    setMessage('');
    try {
      const res = await fetch(`${API}/api/sync/trigger`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type }),
      });
      const d = await res.json();
      setMessage(d.message || `${type} sync triggered`);
      setTimeout(() => fetchStatus(), 3000);
    } catch (e) {
      setMessage('Failed to trigger sync');
    } finally {
      setTriggering('');
    }
  };

  const hasRunning = jobs.some(j => j.status === 'running');

  return (
    <div className="sync-page">
      <div className="page-header-row">
        <div>
          <h1 className="page-title" data-testid="sync-page-title">LMS Data Sync</h1>
          <p className="page-subtitle">Monitor and trigger synchronization with Cypher LMS</p>
        </div>
        <div className="sync-actions">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStatus}
            data-testid="refresh-btn"
            className="gap-1.5"
          >
            <RefreshCw size={13} /> Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => trigger('hourly')}
            disabled={!!triggering || hasRunning}
            data-testid="trigger-hourly-btn"
            className="gap-1.5"
          >
            {triggering === 'hourly' ? <Loader size={13} className="animate-spin" /> : <Play size={13} />}
            Run Hourly Sync
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => trigger('nightly')}
            disabled={!!triggering || hasRunning}
            data-testid="trigger-nightly-btn"
            className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            {triggering === 'nightly' ? <Loader size={13} className="animate-spin" /> : <Play size={13} />}
            Run Full Sync (Nightly)
          </Button>
        </div>
      </div>

      {message && (
        <div className="sync-message" data-testid="sync-message">{message}</div>
      )}

      <div className="sync-schedule-info">
        <p><strong>Hourly Sync</strong>: Users (first 1,000) + All Courses — approx 28 API calls</p>
        <p><strong>Nightly Full Sync</strong>: All Users + All Courses + All Enrollments — runs at 02:00 server time</p>
        <p><strong>Rate Limit</strong>: 80 calls/min (max 100/min from Cypher LMS)</p>
      </div>

      {loading ? (
        <div className="sync-loading"><Loader className="animate-spin" size={24} /></div>
      ) : (
        <>
          <h2 className="section-title">Latest Status per Job</h2>
          {jobs.length === 0 ? (
            <div className="empty-state" data-testid="no-jobs-msg">No sync runs yet. Trigger a sync above.</div>
          ) : (
            <div className="jobs-grid" data-testid="jobs-grid">
              {jobs.map(job => <JobCard key={job.job_name} job={job} />)}
            </div>
          )}

          <h2 className="section-title" style={{ marginTop: 32 }}>Sync History</h2>
          <div className="history-table-wrap" data-testid="history-table">
            <table className="history-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Job</th>
                  <th>Status</th>
                  <th>Processed</th>
                  <th>Inserted</th>
                  <th>Updated</th>
                  <th>Started</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#888' }}>No history yet</td></tr>
                ) : history.map(h => {
                  const duration = h.started_at && h.finished_at
                    ? `${Math.round((new Date(h.finished_at) - new Date(h.started_at)) / 1000)}s`
                    : '—';
                  return (
                    <tr key={h.id} data-testid={`history-row-${h.id}`}>
                      <td style={{ color: '#888', fontSize: 12 }}>{h.id}</td>
                      <td><code style={{ fontSize: 12 }}>{h.job_name}</code></td>
                      <td><StatusBadge status={h.status} /></td>
                      <td>{(h.rows_processed || 0).toLocaleString()}</td>
                      <td style={{ color: '#059669' }}>{(h.rows_inserted || 0).toLocaleString()}</td>
                      <td style={{ color: '#0284c7' }}>{(h.rows_updated || 0).toLocaleString()}</td>
                      <td style={{ fontSize: 12 }}>{fmt(h.started_at)}</td>
                      <td style={{ fontSize: 12 }}>{duration}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
